import { HttpInterceptorFn } from '@angular/common/http';
import { retry, timer } from 'rxjs';

/**
 * Auth and session endpoints that must NOT be retried — an error on these
 * is authoritative and should propagate immediately.
 */
const NO_RETRY_PREFIXES = ['/api/auth/', '/api/auth/me', '/api/auth/login'];

/** HTTP status codes that indicate a transient server-side condition. */
const TRANSIENT_SERVER_STATUSES = new Set([502, 503, 504]);

/** Maximum number of retry attempts after the initial request. */
const MAX_RETRIES = 2;

/** Backoff delay in ms for each retry attempt (index 0 = first retry). */
const BACKOFF_DELAYS_MS = [300, 900];

/**
 * Retries idempotent GET requests up to 2 times on transient failures
 * (status 0/network errors and 502/503/504 only — NOT 4xx).
 * Auth and session endpoints are excluded from retry logic.
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const isGet = req.method === 'GET';
  const isExcluded = NO_RETRY_PREFIXES.some((prefix) => req.url.startsWith(prefix));

  if (!isGet || isExcluded) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: { status?: number }, retryCount) => {
        const status = error?.status ?? 0;
        const isTransient = status === 0 || TRANSIENT_SERVER_STATUSES.has(status);
        if (!isTransient) {
          // Non-transient errors (4xx, 5xx other than 502/503/504) propagate immediately.
          throw error;
        }
        const delayMs =
          BACKOFF_DELAYS_MS[retryCount - 1] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1];
        return timer(delayMs);
      },
    })
  );
};
